import { getIsAgencyManagerActiveData } from "app/redux/features/isAgencyActive";
import { getObjectFromLocalStore } from "app/utils/localStore";
import { setNeioAccessToken } from "lib/utils";
import { TOASTTYPE, USERTYPE } from "app/utils/types";
import { Toast } from "primereact/toast";
import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Chat from "./Chat";
import { makeRequest } from "app/utils/makeRequest";
import { useLocation } from "react-router-dom";
import { capitalizeFirstLetter } from "app/utils/utils";
import { GET_CURR_LOGGED_IN_USER } from "app/redux/features/users/action";
import { getFetchUsers } from "app/redux/features/users";
import { isEmpty } from "ramda";

interface IRootChat {
  children: any;
}

const RootChat: FC<IRootChat> = ({ children }) => {
  const { t } = useTranslation();
  const toast = useRef<Toast>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [currUser, setCurrUser] = useState<any>(null);
  const [neioToken, setNeioToken] = useState<string | null>(null);
  const userAccess = getObjectFromLocalStore("useraccess");
  const { isAgencyManagerActive } = useSelector(getIsAgencyManagerActiveData);
  const [loading, setLoading] = useState({
    currUser: true,
    users: true,
  });

  const { currLoggedInUser } = useSelector(getFetchUsers);
  const dispatch = useDispatch();

  // Ensure Neio token is available and cached
  useEffect(() => {
    const initNeioToken = async () => {
      try {
        const existing = getObjectFromLocalStore("neioAccessToken");
        if (existing) {
          setNeioToken(existing);
          return;
        }
        await setNeioAccessToken();
        const refreshed = getObjectFromLocalStore("neioAccessToken");
        setNeioToken(refreshed || null);
      } catch (e) {
        setNeioToken(null);
      }
    };
    initNeioToken();
  }, []);
  const fetchCurrUser = async (data: any) => {
    let chatBots = [];
    if (userAccess.userType === USERTYPE.CONTACT) {
      // Get bot list
      const query = `pageNumber=1&pageSize=10&sortBy=createdAt&sortOrder=desc`;
      const response = await makeRequest("virtualAdvisors").get(query, false, true);

      // Find the bot with isChat: true
      const virtualAdvisorsList = response?.data?.data || [];

      if (userAccess.userType === USERTYPE.AGENT) {
        chatBots = virtualAdvisorsList;
      } else {
        chatBots = virtualAdvisorsList.filter((bot: any) => bot.isChat === true);
      }
    }

    // Transform the assignedTo.users data into the desired format and log it
    let assignedUsers = [] as any[];
    if (chatBots?.[0]?.assignedTo?.users) {
      assignedUsers = (chatBots?.[0]?.assignedTo?.users || [])
        .filter((user: any) => user.assignmentType === "DIRECT")
        .map((user: any) => ({
          id: user?.id,
          name: user?.name,
          email: user?.email,
        }));
    }

    setCurrUser({
      id: data?.id,
      firstName: (data?.firstName ?? data?.profile?.firstName) || "",
      lastName: (data?.lastName ?? data?.profile?.lastName) || "",
      email: data?.email,
      agentEmail:
        userAccess.userType === USERTYPE.CONTACT ? data?.assignedBot?.[0]?.owner?.email : null,
      profilePictureLink:
        data?.profilePicture ??
        (data?.profile?.profilePicture
          ? data?.profile?.profilePicture
          : `https://ui-avatars.com/api/?name=${encodeURIComponent(
              `${(data?.firstName ?? data?.profile?.firstName) || ""} ${
                (data?.lastName ?? data?.profile?.lastName) || ""
              }`,
            )}&background=random`),
      ...(userAccess.userType === USERTYPE.CONTACT
        ? {
            botadvisors: assignedUsers,
          }
        : {}),
    });
  };

  useEffect(() => {
    const { data, loading: currUserLoading, error } = currLoggedInUser;
    if (!currUserLoading && data) {
      fetchCurrUser(data);
      setLoading((p) => ({ ...p, currUser: false }));
    } else if (!currUserLoading && error) {
      toast?.current?.show({
        severity: TOASTTYPE.ERROR,
        summary: t("agentBranding.label.error"),
        detail: error.message,
        life: 3000,
      });
      setLoading((p) => ({ ...p, currUser: false }));
    }
  }, [currLoggedInUser]);

  // Fetch virtual advisors and add to users
  const fetchVirtualAdvisors = async () => {
    try {
      // Get bot list
      const query = `pageNumber=1&pageSize=10&sortBy=createdAt&sortOrder=desc`;
      const response = await makeRequest("virtualAdvisors").get(query, false, true);

      // Find the bot with isChat: true
      const data = response?.data?.data || [];
      let chatBots = [] as any[];
      if (userAccess.userType === USERTYPE.AGENT) {
        chatBots = data;
      } else {
        chatBots = data.filter((bot: any) => bot.isChat === true);
      }

      // get all users from chatUser API first
      makeRequest("chatUser")
        .get(
          userAccess.userType === USERTYPE.AGENT ? `isAgency=${isAgencyManagerActive}` : "",
          false,
          true,
        )
        .then((res) => {
          const chatUsers = res.data.data?.map((user: any) => ({
            id: user.id,
            firstName: user.profile?.firstName || "",
            lastName: user.profile?.lastName || "",
            email: user.email,
            ...(user.userType === "contact" && user?.assignedBot
              ? {
                  assignedBot: {
                    id: user.assignedBot?.id,
                    name: user.assignedBot?.name,
                  },
                }
              : {}),
            profilePictureLink:
              user.profile?.profilePicture ??
              (user?.profile?.profilePicture
                ? user?.profile?.profilePicture
                : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    `${(user?.firstName ?? user?.profile?.firstName) || ""} ${
                      (user?.lastName ?? user?.profile?.lastName) || ""
                    }`,
                  )}&background=random`),
            userType: user.userType,
            title:
              userAccess.userType === USERTYPE.ADMIN && "isSuperAdmin" in user
                ? user.role?.roleName || (user.isSuperAdmin ? "Super Admin" : "Admin")
                : user.userType === USERTYPE.ADMIN
                ? "Case Manager"
                : user.isAgencyManager && user.parentAgencyId
                ? "Sub-Agency Manager"
                : user.isAgencyManager
                ? "Agency Manager"
                : user.isAssistant
                ? "Assistant"
                : user.userType === USERTYPE.AGENT
                ? "Advisor"
                : capitalizeFirstLetter(user.userType ?? ""),
          }));

          // Add virtual advisor to users after fetching chat users
          if (chatBots) {
            const virtualAdvisors: any[] = [];
            chatBots.forEach((chatBot: any) => {
              const virtualAdvisorData = {
                id: chatBot.botConfigId,
                firstName: chatBot.name,
                lastName: "",
                userType:
                  (chatBot.isActive || chatBot.isActive === null) && chatBot.isChat
                    ? "bot"
                    : "child-bot",
                email: "bot@iapp.cool",
                profilePictureLink: chatBot.avatar?.startsWith("https")
                  ? chatBot.avatar
                  : `data:image/jpeg;base64,${chatBot.avatar}`,
                title: "HIVE",
                role: {
                  roleName: "HIVE",
                },
              };
              virtualAdvisors.push(virtualAdvisorData);
            });
            const combinedUsers = [...chatUsers, ...virtualAdvisors];
            // Combine chat users with virtual advisor
            setUsers(combinedUsers);
          } else {
            // If no virtual advisor, just set chat users
            setUsers(chatUsers);
          }

          setLoading((p) => ({ ...p, users: false }));
        })
        .catch((error) => {
          toast?.current?.show({
            severity: TOASTTYPE.ERROR,
            summary: t("agentBranding.label.error"),
            detail: error.message,
            life: 3000,
          });
          setLoading((p) => ({ ...p, users: false }));
        });
    } catch (error) {
      toast?.current?.show({
        severity: TOASTTYPE.ERROR,
        summary: t("agentBranding.label.error"),
        detail: `Error fetching virtual advisors: ${error}`,
        life: 3000,
      });
    }
  };

  useEffect(() => {
    if ((isEmpty(currLoggedInUser.data) || !currLoggedInUser.data) && !currLoggedInUser.loading) {
      dispatch({
        type: GET_CURR_LOGGED_IN_USER,
      });
    }
  }, [currLoggedInUser]);

  useEffect(() => {
    fetchVirtualAdvisors();
  }, [isAgencyManagerActive]);

  const { pathname } = useLocation();

  // Fix: Always show loading until both currUser and users are loaded
  if (loading.currUser || loading.users || !currUser || !neioToken) {
    return children; // or a loading spinner
  }

  return pathname === "/chat" ? (
    children
  ) : (
    <Chat
      user={currUser}
      peers={users.filter((el: any) => el?.id !== currUser?.id)}
      neioAccessToken={neioToken}
    >
      {children}
    </Chat>
  );
};

export default RootChat;
